import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InboxIcon from "@mui/icons-material/Inbox";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import { TaskRequest } from "src/app/models/screens/task.model";
import TaskRequestsScreenViewModel from "src/app/screens/taskRequests/taskRequests.viewmodel";
import styles from "src/app/styles/screens/taskRequests.style";

const TaskRequestsScreenView: React.FC = () => {
  const {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    adminRequestUsers,
    isLoadingAdminUsers,
    selectedTab,
    onTabChanged,
    receivedRequests,
    isLoadingReceived,
    sentRequests,
    isLoadingSent,
    minutesToLabel,
    getStatusChipColor,
    onAcceptRequest,
    onRejectRequest,
    onAcceptMoreTime,
    onRejectMoreTime,
    canActOnRequests,
    isViewingAsAdmin,
    tags,
  } = TaskRequestsScreenViewModel();

  const renderReceivedRequestCard = (request: TaskRequest) => {
    const hasPendingMoreTime =
      request.moreTimeRequested && request.moreTimeStatus === "Pending";

    return (
      <Grid key={request.id} size={{ xs: 12, md: 6 }}>
        <Paper
          elevation={0}
          sx={[
            styles.requestCard,
            hasPendingMoreTime ? styles.requestCardMoreTime : {},
          ]}
        >
          <Box sx={styles.requestTitleRow}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {request.taskIsPriority && (
                <Tooltip title="Priority task">
                  <PriorityHighIcon fontSize="small" color="error" />
                </Tooltip>
              )}
              <Typography sx={styles.requestTitleText}>
                {request.taskTitle}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={request.status}
              color={getStatusChipColor(request.status)}
            />
          </Box>
          <Box sx={styles.chipRow}>
            <Chip
              size="small"
              label={request.taskTag}
              variant="filled"
              sx={
                (request.taskTagColor ?? tags.get(request.taskTag))
                  ? {
                      backgroundColor:
                        request.taskTagColor ?? tags.get(request.taskTag),
                      color: "#fff",
                    }
                  : {}
              }
            />
            {request.taskIsPriority && (
              <Chip
                size="small"
                label="Priority"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
          <Typography sx={styles.requestMetaText}>
            From: {request.requesterFullName}
          </Typography>
          <Typography sx={styles.requestMetaText}>
            Date: {request.taskDate}
          </Typography>
          <Typography sx={styles.requestMetaText}>
            Time: {minutesToLabel(request.taskFrom)} –{" "}
            {minutesToLabel(request.taskTo)}
          </Typography>
          <Typography sx={styles.requestDescriptionText}>
            {request.taskDescription}
          </Typography>

          {hasPendingMoreTime && (
            <>
              <Divider sx={{ mb: 1 }} />
              <Typography sx={[styles.requestMetaText, { fontWeight: 600 }]}>
                Reschedule requested:
              </Typography>
              <Typography sx={styles.requestMetaText}>
                New Date: {request.moreTimeRequestedDate ?? "—"}
              </Typography>
              <Typography sx={styles.requestMetaText}>
                New Time:{" "}
                {request.moreTimeRequestedFrom !== undefined
                  ? minutesToLabel(request.moreTimeRequestedFrom)
                  : "—"}{" "}
                –{" "}
                {request.moreTimeRequestedTo !== undefined
                  ? minutesToLabel(request.moreTimeRequestedTo)
                  : "—"}
              </Typography>
              {canActOnRequests && (
                <Box sx={styles.requestActionsRow}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => onAcceptMoreTime(request.id)}
                  >
                    Accept Reschedule
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => onRejectMoreTime(request.id)}
                  >
                    Reject
                  </Button>
                </Box>
              )}
            </>
          )}

          {request.status === "Pending" &&
            !request.isDirectAssignment &&
            canActOnRequests && (
              <Box sx={styles.requestActionsRow}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => onAcceptRequest(request.id)}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => onRejectRequest(request.id)}
                >
                  Reject
                </Button>
              </Box>
            )}
          {request.isDirectAssignment && (
            <Chip size="small" label="Direct Assignment" color="primary" />
          )}
        </Paper>
      </Grid>
    );
  };

  const renderSentRequestCard = (request: TaskRequest) => (
    <Grid key={request.id} size={{ xs: 12, md: 6 }}>
      <Paper elevation={0} sx={styles.requestCard}>
        <Box sx={styles.requestTitleRow}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {request.taskIsPriority && (
              <Tooltip title="Priority task">
                <PriorityHighIcon fontSize="small" color="error" />
              </Tooltip>
            )}
            <Typography sx={styles.requestTitleText}>
              {request.taskTitle}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={request.status}
            color={getStatusChipColor(request.status)}
          />
        </Box>
        <Box sx={styles.chipRow}>
          <Chip
            size="small"
            label={request.taskTag}
            variant="filled"
            sx={
              (request.taskTagColor ?? tags.get(request.taskTag))
                ? {
                    backgroundColor:
                      request.taskTagColor ?? tags.get(request.taskTag),
                    color: "#fff",
                  }
                : {}
            }
          />
          {request.taskIsPriority && (
            <Chip
              size="small"
              label="Priority"
              color="error"
              variant="outlined"
            />
          )}
        </Box>
        <Typography sx={styles.requestMetaText}>
          Date: {request.taskDate}
        </Typography>
        <Typography sx={styles.requestMetaText}>
          Time: {minutesToLabel(request.taskFrom)} –{" "}
          {minutesToLabel(request.taskTo)}
        </Typography>
        <Typography sx={styles.requestDescriptionText}>
          {request.taskDescription}
        </Typography>
        {request.moreTimeRequested && (
          <>
            <Divider sx={{ mt: 1, mb: 1 }} />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Reschedule to:{" "}
              <strong>{request.moreTimeRequestedDate ?? "—"}</strong>{" "}
              {request.moreTimeRequestedFrom !== undefined
                ? minutesToLabel(request.moreTimeRequestedFrom)
                : "—"}{" "}
              –{" "}
              {request.moreTimeRequestedTo !== undefined
                ? minutesToLabel(request.moreTimeRequestedTo)
                : "—"}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={`Reschedule: ${request.moreTimeStatus ?? "Pending"}`}
                color={
                  request.moreTimeStatus === "Accepted"
                    ? "success"
                    : request.moreTimeStatus === "Rejected"
                      ? "error"
                      : "warning"
                }
              />
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  );

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Task Requests" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Task Requests
            </Typography>
          </Grid>

          {userRole === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="tr-owner-select-label">
                      Request Owner
                    </InputLabel>
                    <Select
                      labelId="tr-owner-select-label"
                      value={selectedOwnerId}
                      label="Request Owner"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          {userRole === "Admin" && selectedTab === 0 && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="admin-tr-owner-label">
                      Viewing Requests Of
                    </InputLabel>
                    <Select
                      labelId="admin-tr-owner-label"
                      value={selectedOwnerId}
                      label="Viewing Requests Of"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingAdminUsers}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {adminRequestUsers.map((user: RequestManagementUser) => (
                        <MenuItem key={user.id} value={user.id}>
                          {`${user.firstName} ${user.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              <Tabs
                value={selectedTab}
                onChange={onTabChanged}
                variant="fullWidth"
                scrollButtons="auto"
              >
                <Tab
                  icon={<InboxIcon />}
                  iconPosition="start"
                  label="Received"
                />
                <Tab icon={<SendIcon />} iconPosition="start" label="Sent" />
              </Tabs>
              <Divider />

              {selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Received Task Requests
                    {isViewingAsAdmin && " (View Only)"}
                  </Typography>
                  {isLoadingReceived ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {receivedRequests.map(renderReceivedRequestCard)}
                      {receivedRequests.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={styles.noRequestsText}>
                            No received task requests.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}

              {selectedTab === 1 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Sent Task Requests
                  </Typography>
                  {isLoadingSent ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {sentRequests.map(renderSentRequestCard)}
                      {sentRequests.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={styles.noRequestsText}>
                            No sent task requests.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default TaskRequestsScreenView;
